import React, { useEffect, useState } from "react";
import { X, Megaphone, MessageSquare, AlertTriangle, PhoneOff, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { formatRelative } from "@/lib/utils";

/**
 * Historique des diffusions d'assignations pour un événement.
 *
 * Props:
 *  - eventId
 *  - eventName
 *  - onClose()
 */
export default function PublishHistoryModal({ eventId, eventName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/events/${eventId}/publish-history`);
        if (alive) setLogs(data.logs || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Impossible de charger l'historique.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [eventId]);

  const toggle = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const actionMeta = (action) => {
    if (action === "publish") return { label: "Première diffusion", icon: <Megaphone size={14} strokeWidth={2.2} /> };
    if (action === "republish") return { label: "Re-diffusion", icon: <MessageSquare size={14} strokeWidth={2.2} /> };
    return { label: "Annulation", icon: <RotateCcw size={14} strokeWidth={2.2} /> };
  };

  const statusBadge = (st) => {
    if (st === "sent" || st === "mocked") return <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-black/70 bg-black text-white">SMS envoyé</span>;
    if (st === "no_phone") return <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-[var(--border-strong)] text-[var(--text)]">Sans numéro</span>;
    return <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-[var(--border-strong)] text-[var(--text)] line-through">Échec</span>;
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="publish-history-modal"
    >
      <div
        className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative"
        style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-[var(--surface-3)]"
          aria-label="Fermer"
          data-testid="publish-history-close"
        >
          <X size={18} strokeWidth={2.2} />
        </button>

        <div className="label mb-1">Historique des diffusions</div>
        <h3 className="text-xl font-bold tracking-tight mb-1" data-testid="publish-history-title">
          « {eventName} »
        </h3>
        <p className="text-xs text-[var(--muted)] mb-5">
          Trace de chaque envoi SMS d&apos;assignations pour cet événement.
        </p>

        {loading && (
          <div className="text-sm text-[var(--muted)]" data-testid="publish-history-loading">Chargement…</div>
        )}

        {!loading && error && (
          <div className="rounded-xl p-3 text-sm" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
            {error}
          </div>
        )}

        {!loading && !error && logs.length === 0 && (
          <div className="rounded-xl p-4 text-sm text-[var(--muted)]" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }} data-testid="publish-history-empty">
            Aucune diffusion enregistrée pour cet événement.
          </div>
        )}

        {!loading && !error && logs.length > 0 && (
          <ul className="space-y-3" data-testid="publish-history-list">
            {logs.map((log) => {
              const meta = actionMeta(log.action);
              const isOpen = expanded.has(log.id);
              const scopeLabel = log.scope === "date" && log.date
                ? `Journée ${log.date}`
                : "Événement complet";
              const isUnpublish = log.action === "unpublish";
              return (
                <li
                  key={log.id}
                  className="rounded-xl p-4"
                  style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}
                  data-testid={`publish-history-entry-${log.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}>
                        {meta.icon}
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm font-bold truncate">
                          {meta.label} · {scopeLabel}
                          {log.force_all && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border border-black/70 bg-black text-white align-middle">
                              renvoi forcé
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[var(--muted)] truncate">
                          {formatRelative(log.at)} · par {log.by_user_email || "—"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {!isUnpublish && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="rounded-lg p-2 text-center" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                        <div className="flex items-center justify-center gap-1 text-[var(--text)]">
                          <MessageSquare size={12} strokeWidth={2.4} />
                          <span className="text-base font-bold tabular">{log.sms_sent || 0}</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">SMS envoyés</div>
                      </div>
                      <div className="rounded-lg p-2 text-center" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                        <div className="flex items-center justify-center gap-1 text-[var(--text)]">
                          <PhoneOff size={12} strokeWidth={2.4} />
                          <span className="text-base font-bold tabular">{log.no_phone || 0}</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">Sans numéro</div>
                      </div>
                      <div className="rounded-lg p-2 text-center" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                        <div className="flex items-center justify-center gap-1 text-[var(--text)]">
                          <AlertTriangle size={12} strokeWidth={2.4} />
                          <span className="text-base font-bold tabular">{log.failed || 0}</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">Échecs</div>
                      </div>
                    </div>
                  )}

                  {!isUnpublish && (log.notified || []).length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggle(log.id)}
                      className="flex items-center gap-1 text-xs font-semibold text-[var(--text)] hover:underline"
                      data-testid={`publish-history-toggle-${log.id}`}
                    >
                      {isOpen ? <ChevronDown size={14} strokeWidth={2.4} /> : <ChevronRight size={14} strokeWidth={2.4} />}
                      {isOpen ? "Masquer le détail" : `Voir le détail (${log.notified.length} officiel${log.notified.length > 1 ? "s" : ""})`}
                    </button>
                  )}

                  {!isUnpublish && isOpen && (log.notified || []).length > 0 && (
                    <ul className="mt-3 divide-y" style={{ borderColor: "var(--border)" }} data-testid={`publish-history-details-${log.id}`}>
                      {log.notified.map((n, idx) => (
                        <li key={n.official_id || idx} className="py-2 flex items-center justify-between gap-3 text-sm">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{n.name || "—"}</div>
                            <div className="text-xs text-[var(--muted)] truncate">
                              {n.phone || "Aucun numéro"}
                              {n.games_count ? ` · ${n.games_count} partie${n.games_count > 1 ? "s" : ""}` : ""}
                            </div>
                            {n.error && (
                              <div className="text-xs text-[var(--muted)] truncate">Erreur : {n.error}</div>
                            )}
                          </div>
                          <div className="shrink-0">{statusBadge(n.status)}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex items-center justify-end mt-5">
          <button onClick={onClose} className="btn-ghost" data-testid="publish-history-close-btn">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
